import React, { useEffect, useState, useContext } from 'react';
import { Card, Row, Col, Typography, Button, Space, Spin, Tag } from '@douyinfe/semi-ui';
import { IconRefresh, IconPlayCircle, IconBox, IconGift, IconCode } from '@douyinfe/semi-icons';
import { useNavigate } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { API } from '../../helpers';
import { UserContext } from '../../context/User';

const { Title, Text } = Typography;

const chartColors = {
  requests: '#3B82F6',
  quota: '#06B6D4',
  tokens: '#0EA5E9',
};

const barColors = ['#3B82F6', '#06B6D4', '#0EA5E9', '#10B981', '#F59E0B', '#EF4444', '#14B8A6', '#8B5CF6'];

const Dashboard = () => {
  const navigate = useNavigate();
  const [userState] = useContext(UserContext);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const res = await API.get('/api/user/dashboard');
      if (res.data.success) {
        setData(res.data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const processTimeSeriesData = () => {
    const dailyData = {};
    const dates = data.map(item => item.Day);
    const maxDate = new Date();
    let minDate = dates.length > 0 ? new Date(Math.min(...dates.map(d => new Date(d)))) : new Date();
    
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6);
    if (minDate > sevenDaysAgo) minDate = sevenDaysAgo;

    for (let d = new Date(minDate); d <= maxDate; d.setDate(d.getDate() + 1)) {
      const dateStr = d.toISOString().split('T')[0];
      dailyData[dateStr] = { date: dateStr, requests: 0, quota: 0, tokens: 0 };
    }

    data.forEach(item => {
      if (dailyData[item.Day]) {
        dailyData[item.Day].requests += item.RequestCount;
        dailyData[item.Day].quota += item.Quota / 1000000;
        dailyData[item.Day].tokens += item.PromptTokens + item.CompletionTokens;
      }
    });

    return Object.values(dailyData).sort((a, b) => a.date.localeCompare(b.date));
  };

  const processModelData = () => {
    const timeData = {};
    const models = [...new Set(data.map(item => item.ModelName))];
    const dates = data.map(item => item.Day);
    const maxDate = new Date();
    let minDate = dates.length > 0 ? new Date(Math.min(...dates.map(d => new Date(d)))) : new Date();
    
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6);
    if (minDate > sevenDaysAgo) minDate = sevenDaysAgo;

    for (let d = new Date(minDate); d <= maxDate; d.setDate(d.getDate() + 1)) {
      const dateStr = d.toISOString().split('T')[0];
      timeData[dateStr] = { date: dateStr };
      models.forEach(model => { timeData[dateStr][model] = 0; });
    }

    data.forEach(item => {
      if (timeData[item.Day]) {
        timeData[item.Day][item.ModelName] = item.PromptTokens + item.CompletionTokens;
      }
    });

    return Object.values(timeData).sort((a, b) => a.date.localeCompare(b.date));
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('zh-CN', { month: 'numeric', day: 'numeric' });
  };

  const timeSeriesData = processTimeSeriesData();
  const modelData = processModelData();
  const models = [...new Set(data.map(item => item.ModelName))];

  const quickActions = [
    { icon: <IconPlayCircle size="extra-large" style={{ color: '#3B82F6' }} />, title: '在线体验', desc: '与 AI 对话，支持流式响应', path: '/playground' },
    { icon: <IconBox size="extra-large" style={{ color: '#06B6D4' }} />, title: '模型定价', desc: '查看所有模型价格', path: '/models' },
    { icon: <IconGift size="extra-large" style={{ color: '#10B981' }} />, title: '邀请奖励', desc: '邀请好友获取奖励', path: '/referral' },
  ];

  return (
    <div style={{ padding: '24px', background: '#f5f7fa', minHeight: 'calc(100vh - 60px)' }}>
      {/* 页面标题区域 - 参考首页风格 */}
      <div style={{ 
        marginBottom: '24px',
        padding: '24px',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: '12px',
        color: '#fff',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <h2 style={{ margin: '0 0 8px 0', fontSize: '24px', fontWeight: '600' }}>
            📊 控制台
          </h2>
          <p style={{ margin: 0, fontSize: '14px', opacity: 0.9 }}>
            欢迎回来，{userState?.username || '用户'}！这是您的 API 网关概览
          </p>
        </div>
        <Button 
          icon={<IconRefresh />} 
          onClick={fetchDashboardData} 
          loading={loading}
          style={{ 
            background: 'rgba(255, 255, 255, 0.2)',
            color: '#fff',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            backdropFilter: 'blur(10px)',
            borderRadius: '8px'
          }}
        >
          刷新数据
        </Button>
      </div>

      {/* 推广横幅 */}
      <Card
        style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          marginBottom: 24,
          borderRadius: 12,
          border: 'none',
          boxShadow: '0 4px 16px rgba(fff, fff, fff, 0.3)'
        }}
        bodyStyle={{ padding: 24 }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title heading={3} style={{ margin: '0 0 8px 0', color: '#fff' }}>
              立即体验 AI 模型 <Tag style={{ marginLeft: 8, background: 'rgba(255,255,255,0.3)', color: '#fff', border: 'none' }}>New</Tag>
            </Title>
            <Text style={{ color: 'rgba(255,255,255,0.9)' }}>
              即时测试 GPT-4o、Claude、Sora 等 100+ 模型,支持流式响应!
            </Text>
          </div>
          <Button 
            style={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: '#fff',
              fontWeight: 'bold',
              borderRadius: '8px',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
            }}
            onClick={() => navigate('/playground')}
          >
            打开在线体验 →
          </Button>
        </div>
      </Card>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          {/* 三个并排的折线图 */}
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            {[
              { title: '📈 请求数', dataKey: 'requests', color: chartColors.requests },
              { title: '💰 消费额度 ($)', dataKey: 'quota', color: chartColors.quota },
              { title: '🔢 Token 数', dataKey: 'tokens', color: chartColors.tokens },
            ].map(chart => (
              <Col key={chart.dataKey} span={8} lg={8} md={12} sm={24}>
                <Card style={{ 
                  borderRadius: '8px', 
                  height: 220,
                  border: '1px solid #e4e7ed',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
                }}>
                  <Title heading={5} style={{ marginBottom: 8 }}>{chart.title}</Title>
                  <ResponsiveContainer width="100%" height={160}>
                    <LineChart data={timeSeriesData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                      <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                      <YAxis hide />
                      <Tooltip labelFormatter={formatDate} />
                      <Line type="monotone" dataKey={chart.dataKey} stroke={chart.color} strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </Card>
              </Col>
            ))}
          </Row>

          {/* 模型使用统计 */}
          <Card style={{ 
            marginBottom: 16, 
            borderRadius: '8px',
            border: '1px solid #e4e7ed',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
          }}>
            <Title heading={5} style={{ marginBottom: 16 }}>📊 模型使用统计</Title>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={modelData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip labelFormatter={formatDate} />
                {models.map((model, index) => (
                  <Bar key={model} dataKey={model} stackId="a" fill={barColors[index % barColors.length]} name={model} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </Card>

          {/* 快速操作 */}
          <Row gutter={[16, 16]}>
            {quickActions.map((action, index) => (
              <Col key={index} span={8} lg={8} md={12} sm={24}>
                <Card
                  style={{ 
                    borderRadius: '8px', 
                    cursor: 'pointer', 
                    textAlign: 'center',
                    border: '1px solid #e4e7ed',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                    transition: 'all 0.3s ease'
                  }}
                  hoverable
                  onClick={() => navigate(action.path)}
                  bodyStyle={{ padding: '24px 16px' }}
                >
                  <div style={{ marginBottom: 12 }}>{action.icon}</div>
                  <Title heading={5} style={{ marginBottom: 4 }}>{action.title}</Title>
                  <Text type="tertiary" size="small">{action.desc}</Text>
                </Card>
              </Col>
            ))}
          </Row>

          {/* 代码示例 */}
          <Card style={{ 
            marginTop: 16, 
            borderRadius: '8px',
            border: '1px solid #e4e7ed',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <Title heading={5} style={{ margin: 0 }}>
                <IconCode style={{ marginRight: 8, color: '#667eea' }} />
                快速开始
              </Title>
              <Button 
                size="small"
                onClick={() => {
                  const code = `import openai

client = openai.OpenAI(
    api_key="your-api-key",
    base_url="https://opcache.com/v1"
)

response = client.chat.completions.create(
    model="gpt-4o",
    messages=[{"role": "user", "content": "Hello!"}]
)

print(response.choices[0].message.content)`;
                  navigator.clipboard.writeText(code);
                }}
                style={{ 
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: '#fff',
                  fontWeight: 'bold',
                  borderRadius: '6px',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)'
                }}
              >
                复制代码
              </Button>
            </div>
            <pre style={{
              background: '#1e293b',
              borderRadius: 8,
              padding: 20,
              fontFamily: 'monospace',
              fontSize: 13,
              color: '#e2e8f0',
              overflow: 'auto',
              margin: 0
            }}>
{`import openai

client = openai.OpenAI(
    api_key="your-api-key",
    base_url="https://opcache.com/v1"
)

response = client.chat.completions.create(
    model="gpt-4o",
    messages=[{"role": "user", "content": "Hello!"}]
)

print(response.choices[0].message.content)`}
            </pre>
          </Card>
        </>
      )}
    </div>
  );
};

export default Dashboard;
